// Pure grouping for the admin vendor detail page: questionnaire tabs with
// category sections, the consolidated Questionnaires (grouped) tab, and the Compliance
// Documents rows. Kept free of React (same pattern as wizard.ts) so the tab
// model can be unit-tested directly — the app has no DOM test environment.

import { orderWithInstances } from './wizard.ts';
import type { AdminQuestionnaire, Question, QuestionCategory } from './types.ts';

export type CategorySection = { category: QuestionCategory; questions: Question[] };

export type QuestionnaireGroup = {
  questionnaire: AdminQuestionnaire;
  sections: CategorySection[];
};

export type ComplianceDocRow = { question: Question; categoryName: string };

export type CompanyDetailTabs = {
  /** Questionnaires configured to show as their own tab (applicable only), in display_order. */
  questionnaireTabs: QuestionnaireGroup[];
  /** Consolidated "Questionnaires" tab: applicable non-own-tab questionnaires (sent to the vendor)
   *  plus retired-but-answered questionnaires, in display_order. */
  groupedTab: QuestionnaireGroup[];
  /** Compliance-flagged questions once approved — or while a renewal is awaiting
   *  re-verification (reverifyIds) — the Compliance Documents table, deduped. */
  complianceDocs: ComplianceDocRow[];
  /** Every compliance-flagged question in play (approved or not), deduped — sources the admin upload-on-behalf picker and the tab's empty-state branching. */
  allComplianceDocs: ComplianceDocRow[];
  /** Visible non-doc questions whose category row is missing (data-integrity fallback). */
  orphanQuestions: Question[];
};

// Label shown in the Compliance Documents table for questions whose category
// row no longer exists.
export const UNKNOWN_CATEGORY_LABEL = '—';

// Membership in the Compliance Documents tab is driven by an explicit
// per-question flag (migration 0030), replacing the old "type === document_upload"
// heuristic. Existing document_upload questions were backfilled to true.
function isComplianceDocQuestion(q: Question): boolean {
  return q.include_in_compliance_documents;
}

/**
 * The question set the admin page's progress line counts: questions whose
 * questionnaire currently applies, plus true orphans (missing category row) so
 * an orphaned required question still counts and stays reachable. Questions in
 * non-applicable or non-company questionnaires are excluded — matching the
 * vendor portal's server-side progress computation (which filters by
 * questionnaire_id, migration 0032).
 *
 * Assumes parent/child (condition) links never cross questionnaires, so the
 * returned set stays closed under parent_id and downstream visibility checks
 * can always resolve a question's parent.
 */
export function questionsForProgress(
  questionnaires: AdminQuestionnaire[],
  categories: QuestionCategory[],
  questions: Question[],
): Question[] {
  const applicable = new Set(questionnaires.filter((x) => x.applicable).map((x) => x.id));
  const catById = new Map(categories.map((c) => [c.id, c]));
  return questions.filter((q) => {
    if (!catById.has(q.category_id)) return true; // orphan: keep counted & reachable
    // A question belongs to exactly one questionnaire (migration 0032); count it
    // when that questionnaire currently applies to the vendor.
    return q.questionnaire_id !== null && applicable.has(q.questionnaire_id);
  });
}

/**
 * Group questions for the vendor detail tabs.
 *
 * - Applicable questionnaires contribute visible questions; non-applicable
 *   ones contribute only answered questions (visibility is not re-evaluated
 *   for retired questionnaires — what was answered is shown).
 * - compliance-flagged questions always stay inline in their category section
 *   (like any other question). Once their submission is approved (approvedIds)
 *   — or while a renewal is awaiting re-verification (reverifyIds) — they are
 *   additionally mirrored into `complianceDocs` (with their category name) so
 *   they also appear in the Compliance Documents table — they are never moved
 *   out of the questionnaire. Every compliance-flagged question (approved or
 *   not) is collected into `allComplianceDocs`.
 * - A question belongs to exactly one questionnaire (its questionnaire_id,
 *   migration 0032) and renders only there, even when its category is a shared
 *   grouping ticked into several questionnaires. The category supplies the
 *   sub-tab (a category may appear under more than one questionnaire), but each
 *   questionnaire shows only its own questions.
 * - Questions whose questionnaire_id is null or not a known questionnaire (e.g.
 *   another audience) are dropped; questions that claim a known questionnaire
 *   but whose category row is missing or isn't linked there surface as orphans
 *   so they are never silently unreachable.
 */
export function buildCompanyDetailTabs(
  questionnaires: AdminQuestionnaire[],
  categories: QuestionCategory[],
  questions: Question[],
  visibleIds: Set<string>,
  answeredIds: Set<string>,
  approvedIds: Set<string>,
  reverifyIds: Set<string>,
): CompanyDetailTabs {
  const catById = new Map(categories.map((c) => [c.id, c]));
  const questionnaireById = new Map(questionnaires.map((x) => [x.id, x]));

  // Categories per questionnaire in display_order (the API orders them, but
  // sort defensively so grouping never depends on fetch order). A shared
  // category is listed under every known questionnaire it is linked to.
  const catsByQuestionnaire = new Map<string, QuestionCategory[]>();
  for (const c of [...categories].sort((a, b) => a.display_order - b.display_order)) {
    for (const questionnaireId of c.questionnaire_ids) {
      if (!questionnaireById.has(questionnaireId)) continue;
      const list = catsByQuestionnaire.get(questionnaireId) ?? [];
      list.push(c);
      catsByQuestionnaire.set(questionnaireId, list);
    }
  }

  // Questions bucketed by their (questionnaire, category) home, with per-option
  // mirrors seated next to their authored row (same ordering the wizard and the
  // old per-category tabs used). A question renders only under its own
  // questionnaire_id (migration 0032), so a shared category shows different
  // questions in each questionnaire it is ticked into.
  const questionsByQnCat = new Map<string, Map<string, Question[]>>();
  const orphanPool: Question[] = [];
  for (const question of orderWithInstances(questions)) {
    const qn = question.questionnaire_id ? questionnaireById.get(question.questionnaire_id) : undefined;
    if (!qn) continue; // belongs to no known questionnaire (other audience / unassigned)
    const cat = catById.get(question.category_id);
    if (!cat || !cat.questionnaire_ids.includes(qn.id)) {
      orphanPool.push(question); // claims a known questionnaire but category missing / not linked there
      continue;
    }
    const byCat = questionsByQnCat.get(qn.id) ?? new Map<string, Question[]>();
    const list = byCat.get(cat.id) ?? [];
    list.push(question);
    byCat.set(cat.id, list);
    questionsByQnCat.set(qn.id, byCat);
  }

  const questionnaireTabs: QuestionnaireGroup[] = [];
  const groupedTab: QuestionnaireGroup[] = [];
  // Approved compliance docs (the Compliance Documents table) vs every
  // compliance-flagged question in play (sources the upload-on-behalf picker).
  const complianceDocs: ComplianceDocRow[] = [];
  const allComplianceDocs: ComplianceDocRow[] = [];
  const seenComplianceIds = new Set<string>();

  const ordered = [...questionnaires].sort((a, b) => a.display_order - b.display_order);
  for (const questionnaire of ordered) {
    // Applicable questionnaires show what the vendor can currently see;
    // retired ones show what was actually answered.
    const include = questionnaire.applicable
      ? (q: Question) => visibleIds.has(q.id)
      : (q: Question) => answeredIds.has(q.id);

    const byCat = questionsByQnCat.get(questionnaire.id);
    const sections: CategorySection[] = [];
    for (const category of catsByQuestionnaire.get(questionnaire.id) ?? []) {
      const sectionQuestions: Question[] = [];
      for (const question of (byCat?.get(category.id) ?? []).filter(include)) {
        // Every visible question stays inline in its category section. A
        // compliance doc additionally surfaces in the Compliance Documents
        // table once approved — or while a renewal is awaiting
        // re-verification (reverifyIds) — it is mirrored there, never moved out.
        sectionQuestions.push(question);
        if (isComplianceDocQuestion(question) && !seenComplianceIds.has(question.id)) {
          seenComplianceIds.add(question.id);
          allComplianceDocs.push({ question, categoryName: category.name });
          if (approvedIds.has(question.id) || reverifyIds.has(question.id)) {
            complianceDocs.push({ question, categoryName: category.name });
          }
        }
      }
      if (sectionQuestions.length > 0) sections.push({ category, questions: sectionQuestions });
    }

    // No visible questions → no tab. (Compliance docs now stay inline, so a
    // questionnaire keeps its tab as long as it has any visible question,
    // approved or not.)
    if (sections.length === 0) continue;
    // Own-tab only when explicitly configured AND currently applicable. Everything
    // else folds into the consolidated "Questionnaires" tab: applicable non-own-tab
    // questionnaires (already sent — `applicable` implies on-invite-applicable or
    // manually assigned) and retired-but-answered ones (their sections were built
    // from answeredIds, so a non-empty group means the vendor answered it).
    if (questionnaire.display_as_own_tab && questionnaire.applicable) {
      questionnaireTabs.push({ questionnaire, sections });
    } else {
      groupedTab.push({ questionnaire, sections });
    }
  }

  // Orphans render under a trailing "Additional questions" tab; every orphan
  // stays reviewable there, and an approved orphan compliance doc — or one
  // awaiting re-verification (reverifyIds) — is also mirrored into the
  // Compliance Documents table.
  const visibleOrphans = orphanPool.filter((q) => visibleIds.has(q.id));
  for (const question of visibleOrphans) {
    if (isComplianceDocQuestion(question) && !seenComplianceIds.has(question.id)) {
      seenComplianceIds.add(question.id);
      allComplianceDocs.push({ question, categoryName: UNKNOWN_CATEGORY_LABEL });
      if (approvedIds.has(question.id) || reverifyIds.has(question.id)) {
        complianceDocs.push({ question, categoryName: UNKNOWN_CATEGORY_LABEL });
      }
    }
  }

  return {
    questionnaireTabs,
    groupedTab,
    complianceDocs,
    allComplianceDocs,
    // All visible orphans stay reachable here; approved compliance-doc orphans
    // are additionally mirrored into complianceDocs above.
    orphanQuestions: visibleOrphans,
  };
}
